import classNames from 'classnames';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getFirstLocationPart } from '../../util/maps';
import ExternalLink from '../ExternalLink/ExternalLink';
import { BookedByPreview } from '../../components';

import css from './OrderBreakdown.module.css';

const BookingDetails = props => {
  const { booking, listing, savedListingPublicData, transaction, bookings, isProvider } = props;
  const intl = useIntl();
  
  // Try to get seats from multiple sources:
  // 1. booking.attributes (normal flow)
  // 2. transaction.attributes.protectedData (for provider UI when displaying transaction)
  const seatsFromBooking = booking?.attributes?.seats;
  const protectedData = transaction?.attributes?.protectedData || {};
  const adults = protectedData.adults || 0;
  const children = protectedData.children || 0;
  const seatsFromProtectedData = protectedData.seats || (adults > 0 || children > 0 ? adults + children : null);
  const seats = seatsFromBooking || seatsFromProtectedData;
  
  const { publicData } = listing.attributes;
  const {
    activity: listingActivity,
    discipline: listingDiscipline,
    type: listingType,
    groupType: listingGroupType,
    languages: listingLanguages,
    ability: listingAbility,
    meetingLocation: listingMeetingLocation,
    meetingLocationOrigin: listingMeetingLocationOrigin,
    city: listingCity,
  } = publicData || {};

  // The saved listing data is needed because we want
  // to show a 'snapshot' of the listing data
  const {
    activity: savedActivity,
    discipline: savedDiscipline,
    type: savedType,
    groupType: savedGroupType,
    languages: savedLanguages,
    ability: savedAbility,
    meetingLocation: savedMeetingLocation,
    meetingLocationOrigin: savedMeetingLocationOrigin,
    city: savedCity,
  } = savedListingPublicData || {};

  const activity = savedActivity || listingActivity;
  const discipline = savedDiscipline || listingDiscipline;
  const type = savedType || listingType;
  const groupType = savedGroupType || listingGroupType;
  const languages = savedLanguages || listingLanguages;
  const ability = savedAbility || listingAbility;
  const meetingLocation = savedMeetingLocation || listingMeetingLocation;
  const meetingLocationOrigin = savedMeetingLocationOrigin || listingMeetingLocationOrigin;
  const meetingLocationGMapsLink =
    meetingLocationOrigin?.lat &&
    meetingLocationOrigin?.lng &&
    `https://www.google.com/maps/search/${meetingLocationOrigin.lat}+${meetingLocationOrigin.lng}`;

  const city = savedCity || listingCity;
  
  // Get peopleAtMost for provider view
  const peopleAtMost = publicData?.peopleAtMost || savedListingPublicData?.peopleAtMost;
  
  // For provider view, show group overview with booked-by preview
  const of = intl.formatMessage({ id: 'General.of' });
  const people = intl.formatMessage({ id: 'General.people' });

  const detailsToShow = useMemo(
    () => {
      const baseDetails = [
        { labelTranslationKey: 'activity', valueTranslationKey: activity },
        { labelTranslationKey: 'discipline', valueTranslationKey: discipline },
        { labelTranslationKey: 'type', valueTranslationKey: type },
        { labelTranslationKey: 'session', valueTranslationKey: groupType },
        { labelTranslationKey: 'languages', multipleValueTranslationKeys: languages },
        { labelTranslationKey: 'ability', valueTranslationKey: ability },
        {
          labelTranslationKey: 'meetingPlace',
          rawValue: meetingLocationGMapsLink ? (
            <ExternalLink key="linkToTwitter" href={meetingLocationGMapsLink} title="Google Maps">
              {city || getFirstLocationPart(meetingLocation)}
            </ExternalLink>
          ) : (
            city || getFirstLocationPart(meetingLocation)
          ),
        },
        ...(seats ? [{ labelTranslationKey: 'people', rawValue: seats }] : []),
      ];
      
      // Add "Group" field for providers only
      if (isProvider && seats && peopleAtMost) {
        const bookedSeatsString = `${seats} ${of} ${peopleAtMost} ${people}`;
        const groupValue = bookings?.length ? (
          <span className={css.itemBookedSeats}>
            {bookedSeatsString}
            <BookedByPreview bookings={bookings} className={css.bookingsPreview} />
          </span>
        ) : bookedSeatsString;
        
        baseDetails.push({ labelTranslationKey: 'groupSize', rawValue: groupValue });
      }
      
      return baseDetails;
    },
    [
      ability,
      activity,
      bookings,
      city,
      discipline,
      groupType,
      isProvider,
      languages,
      meetingLocation,
      meetingLocationGMapsLink,
      of,
      people,
      peopleAtMost,
      seats,
      type,
    ]
  );

  const details = useMemo(
    () =>
      detailsToShow.map(detail => ({
        label: <FormattedMessage id={`General.${detail.labelTranslationKey}`} />,
        value:
          detail.rawValue ||
          detail.multipleValueTranslationKeys?.map((singleValue, i) => (
            <React.Fragment key={`${singleValue}.${i}`}>
              <FormattedMessage id={`General.${singleValue}`} />
              {i < detail.multipleValueTranslationKeys.length - 1 ? ', ' : ''}
            </React.Fragment>
          )) ||
          (detail.valueTranslationKey && (
            <FormattedMessage id={`General.${detail.valueTranslationKey}`} />
          )),
      })),
    [detailsToShow]
  );

  return (
    <section className={classNames({ [css.bookingBreakdownSection]: seats })}>
      <h3 className={css.breakdownTitle}>
        <FormattedMessage id="BookingBreakdown.bookingDetails" />
      </h3>
      {details.map((detail, i) => (
        <div className={css.lineItem} key={`${detail.key}.${i}`}>
          <span className={css.itemLabel}>{detail.label}</span>
          <span className={classNames(css.itemValue, css.itemGray)}>{detail.value}</span>
        </div>
      ))}
    </section>
  );
};

export default BookingDetails;
